import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, User, Calendar, DollarSign, Wrench, MapPin,
  Mail, Phone, Clock, CheckCircle, Star, MessageSquare,
  Send, Trash2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const SEGMENT_CONFIG = {
  vip: { label: 'VIP', color: '#fac775', bg: 'rgba(250,199,117,0.12)', border: 'rgba(250,199,117,0.25)' },
  regulier: { label: 'Régulier', color: '#00E01A', bg: 'rgba(0,224,26,0.1)', border: 'rgba(0,224,26,0.25)' },
  decouverte: { label: 'Découverte', color: '#3B82F6', bg: 'rgba(59,130,246,0.1)', border: 'rgba(59,130,246,0.25)' },
  inactif: { label: 'Inactif', color: '#8C8F94', bg: 'rgba(140,143,148,0.1)', border: 'rgba(140,143,148,0.25)' },
};

const STATUS_BADGE = {
  'confirmé': { color: '#00E01A', bg: '#142915' },
  'annulé': { color: '#EF4444', bg: '#2A1515' },
  'payé': { color: '#00E01A', bg: '#142915' },
  'acompte': { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
  'en attente': { color: '#F59E0B', bg: '#2A2010' },
  'ouvert': { color: '#F59E0B', bg: '#2A2010' },
  'en_cours': { color: '#3B82F6', bg: 'rgba(59,130,246,0.15)' },
  'résolu': { color: '#00E01A', bg: '#142915' },
  'fermé': { color: '#8C8F94', bg: 'rgba(140,143,148,0.1)' },
};

function formatCurrency(n) {
  return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(n || 0);
}

const TABS = [
  { key: 'overview', label: 'Aperçu', icon: User },
  { key: 'bookings', label: 'Réservations', icon: Calendar },
  { key: 'payments', label: 'Paiements', icon: DollarSign },
  { key: 'exchanges', label: 'Échanges', icon: MessageSquare },
];

export default function ExploitationClientDetail() {
  const { clientId } = useParams();
  const { api } = useAuth();
  const [tab, setTab] = useState('overview');
  const [client, setClient] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [payments, setPayments] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [exchanges, setExchanges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState('');
  const [savingNotes, setSavingNotes] = useState(false);
  const [newExchange, setNewExchange] = useState({ contenu: '', sujet: '', type: 'email', auteur: 'Riding Watt' });
  const [sendingExchange, setSendingExchange] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [clientRes, bookingsRes, paymentsRes, ticketsRes, exchangesRes] = await Promise.all([
        api.get(`/exploitation/clients/${clientId}`),
        api.get(`/exploitation/clients/${clientId}/bookings`),
        api.get(`/exploitation/clients/${clientId}/payments`),
        api.get(`/exploitation/clients/${clientId}/tickets`),
        api.get(`/exploitation/clients/${clientId}/exchanges`),
      ]);
      setClient(clientRes.data);
      setBookings(bookingsRes.data);
      setPayments(paymentsRes.data);
      setTickets(ticketsRes.data);
      setExchanges(exchangesRes.data);
      setNotes(clientRes.data.notes || '');
    } catch (e) {
      console.error(e);
      toast.error('Erreur chargement client');
    } finally { setLoading(false); }
  }, [api, clientId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleSaveNotes = async () => {
    setSavingNotes(true);
    try {
      await api.put(`/exploitation/clients/${clientId}/notes`, null, { params: { notes } });
      toast.success('Notes sauvegardées');
    } catch { toast.error('Erreur'); }
    finally { setSavingNotes(false); }
  };

  const handleAddExchange = async () => {
    if (!newExchange.contenu.trim()) return;
    setSendingExchange(true);
    try {
      await api.post(`/exploitation/clients/${clientId}/exchanges`, {
        ...newExchange,
        date: new Date().toISOString().split('T')[0],
      });
      setNewExchange({ contenu: '', sujet: '', type: 'email', auteur: 'Riding Watt' });
      const res = await api.get(`/exploitation/clients/${clientId}/exchanges`);
      setExchanges(res.data);
      toast.success('Échange ajouté');
    } catch { toast.error('Erreur'); }
    finally { setSendingExchange(false); }
  };

  const handleDeleteExchange = async (exchangeId) => {
    if (!window.confirm('Supprimer cet échange ?')) return;
    try {
      await api.delete(`/exploitation/clients/${clientId}/exchanges/${exchangeId}`);
      setExchanges(prev => prev.filter(e => e.id !== exchangeId));
      toast.success('Échange supprimé');
    } catch { toast.error('Erreur'); }
  };

  if (loading) return <div className="p-6 flex items-center justify-center min-h-[50vh]"><div className="w-8 h-8 border-2 border-[#00E01A] border-t-transparent rounded-full animate-spin" /></div>;
  if (!client) return <div className="p-6 text-[#8C8F94]">Client non trouvé</div>;

  const seg = SEGMENT_CONFIG[client.segment] || SEGMENT_CONFIG.inactif;

  const Badge = ({ status }) => {
    const s = STATUS_BADGE[status] || { color: '#8C8F94', bg: 'rgba(140,143,148,0.1)' };
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider" style={{ backgroundColor: s.bg, color: s.color }}>
        {status}
      </span>
    );
  };

  return (
    <div className="p-4 md:p-6 space-y-5" data-testid="exploitation-client-detail">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Link to="/exploitation/clients" className="mt-1 p-2 rounded-lg text-[#8C8F94] hover:text-white hover:bg-[#1D2327] transition-colors" data-testid="back-btn">
          <ArrowLeft size={20} />
        </Link>
        <div className="flex-1">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-full flex items-center justify-center text-lg font-bold"
              style={{ backgroundColor: seg.bg, color: seg.color, border: `2px solid ${seg.border}` }}>
              {client.first_name?.[0]}{client.last_name?.[0]}
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-white tracking-wide" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
                {client.first_name} {client.last_name}
              </h1>
              <div className="flex items-center gap-3 mt-1">
                <span className="px-2.5 py-0.5 rounded-full text-xs font-semibold uppercase tracking-wider"
                  style={{ backgroundColor: seg.bg, color: seg.color, border: `1px solid ${seg.border}` }}>
                  {seg.label}
                </span>
                {client.email && (
                  <span className="text-[#8C8F94] text-sm flex items-center gap-1"><Mail size={12} /> {client.email}</span>
                )}
                {client.phone && (
                  <span className="text-[#8C8F94] text-sm flex items-center gap-1"><Phone size={12} /> {client.phone}</span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Quick stats */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="client-stats">
        <div className="bg-[#111111] rounded-xl border border-[#222] p-3 text-center">
          <p className="text-xl font-bold text-[#00E01A]" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{formatCurrency(client.total_ca)}</p>
          <p className="text-[9px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>CA total</p>
        </div>
        <div className="bg-[#111111] rounded-xl border border-[#222] p-3 text-center">
          <p className="text-xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{client.total_sessions}</p>
          <p className="text-[9px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Sessions</p>
        </div>
        <div className="bg-[#111111] rounded-xl border border-[#222] p-3 text-center">
          <p className="text-xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>{client.sites?.length || 0}</p>
          <p className="text-[9px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Sites</p>
        </div>
        <div className="bg-[#111111] rounded-xl border border-[#222] p-3 text-center">
          <p className="text-xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {client.first_booking ? new Date(client.first_booking).toLocaleDateString('fr-FR', { month: 'short', year: '2-digit' }) : '—'}
          </p>
          <p className="text-[9px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>1ère résa</p>
        </div>
        <div className="bg-[#111111] rounded-xl border border-[#222] p-3 text-center">
          <p className="text-xl font-bold text-white" style={{ fontFamily: "'Bebas Neue', sans-serif" }}>
            {client.last_activity ? new Date(client.last_activity).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }) : '—'}
          </p>
          <p className="text-[9px] uppercase tracking-wider text-[#8C8F94]" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Dernière act.</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-[#0a0a0a] rounded-xl p-1 border border-[#222]" data-testid="client-tabs">
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all flex-1 justify-center ${
              tab === t.key
                ? 'bg-[#00E01A] text-black shadow-[0_0_12px_rgba(0,224,26,0.2)]'
                : 'text-[#8C8F94] hover:text-white hover:bg-white/5'
            }`}
            data-testid={`tab-${t.key}`}
          >
            <t.icon size={16} />
            <span className="hidden sm:inline">{t.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="min-h-[300px]">
        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="tab-content-overview">
            {/* Info */}
            <div className="bg-[#111111] rounded-xl border border-[#222] p-5 space-y-4">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Informations</h3>
              <div className="space-y-3">
                {client.email && (
                  <div className="flex items-center gap-3">
                    <Mail size={14} className="text-[#52555A]" />
                    <a href={`mailto:${client.email}`} className="text-white text-sm hover:text-[#00E01A] transition-colors">{client.email}</a>
                  </div>
                )}
                {client.phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={14} className="text-[#52555A]" />
                    <a href={`tel:${client.phone}`} className="text-white text-sm hover:text-[#00E01A] transition-colors">{client.phone}</a>
                  </div>
                )}
                <div className="flex items-center gap-3">
                  <MapPin size={14} className="text-[#52555A]" />
                  <span className="text-white text-sm">{client.sites?.join(', ') || 'Aucun site'}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar size={14} className="text-[#52555A]" />
                  <span className="text-white text-sm">Inscrit le {client.created_at ? new Date(client.created_at).toLocaleDateString('fr-FR') : '—'}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="bg-[#111111] rounded-xl border border-[#222] p-5 space-y-3">
              <h3 className="text-white font-semibold text-sm uppercase tracking-wider" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Notes internes</h3>
              <textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                rows={5}
                className="w-full bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-white text-sm resize-none focus:border-[#00E01A] outline-none placeholder:text-[#52555A]"
                placeholder="Ajouter des notes sur ce client..."
                data-testid="client-notes"
              />
              <Button
                onClick={handleSaveNotes}
                disabled={savingNotes}
                className="bg-[#00E01A] hover:bg-[#00B815] text-black text-sm"
                data-testid="save-notes-btn"
              >
                {savingNotes ? 'Sauvegarde...' : 'Sauvegarder'}
              </Button>
            </div>

            {/* Recent tickets */}
            {tickets.length > 0 && (
              <div className="bg-[#111111] rounded-xl border border-[#222] p-5 lg:col-span-2">
                <h3 className="text-white font-semibold text-sm uppercase tracking-wider mb-3" style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>Tickets SAV ({tickets.length})</h3>
                <div className="space-y-2">
                  {tickets.map(t => (
                    <div key={t.id} className="flex items-center justify-between bg-[#0a0a0a] rounded-lg p-3">
                      <div className="flex items-center gap-3">
                        <span className="text-white font-mono text-xs">{t.ticket_number}</span>
                        <span className="text-white text-sm">{t.subject}</span>
                      </div>
                      <Badge status={t.status} />
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* BOOKINGS */}
        {tab === 'bookings' && (
          <div data-testid="tab-content-bookings">
            {bookings.length === 0 ? (
              <div className="bg-[#111111] rounded-xl border border-[#222] p-12 text-center">
                <Calendar size={36} className="mx-auto text-[#52555A] mb-3" />
                <p className="text-[#8C8F94]">Aucune réservation</p>
              </div>
            ) : (
              <div className="bg-[#111111] rounded-xl border border-[#222] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#222]">
                        <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Date</th>
                        <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Site</th>
                        <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Activité</th>
                        <th className="text-center p-3 text-[#8C8F94] text-xs font-semibold uppercase">Durée</th>
                        <th className="text-right p-3 text-[#8C8F94] text-xs font-semibold uppercase">Montant</th>
                        <th className="text-center p-3 text-[#8C8F94] text-xs font-semibold uppercase">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bookings.map(b => (
                        <tr key={b.id} className="border-b border-[#222] last:border-0 hover:bg-white/[0.02]">
                          <td className="p-3 text-white text-sm font-mono">{new Date(b.date).toLocaleDateString('fr-FR')}</td>
                          <td className="p-3 text-[#8C8F94] text-sm">{b.site_id}</td>
                          <td className="p-3 text-white text-sm">{b.activity_type}</td>
                          <td className="p-3 text-center text-[#8C8F94] text-sm">{b.duration_minutes} min</td>
                          <td className="p-3 text-right text-white font-semibold text-sm">{formatCurrency(b.amount)}</td>
                          <td className="p-3 text-center"><Badge status={b.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* PAYMENTS */}
        {tab === 'payments' && (
          <div data-testid="tab-content-payments">
            {payments.length === 0 ? (
              <div className="bg-[#111111] rounded-xl border border-[#222] p-12 text-center">
                <DollarSign size={36} className="mx-auto text-[#52555A] mb-3" />
                <p className="text-[#8C8F94]">Aucun paiement</p>
              </div>
            ) : (
              <div className="bg-[#111111] rounded-xl border border-[#222] overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-[#222]">
                        <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Date</th>
                        <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Site</th>
                        <th className="text-left p-3 text-[#8C8F94] text-xs font-semibold uppercase">Produit</th>
                        <th className="text-right p-3 text-[#8C8F94] text-xs font-semibold uppercase">Montant</th>
                        <th className="text-center p-3 text-[#8C8F94] text-xs font-semibold uppercase">Statut</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map(p => (
                        <tr key={p.id} className="border-b border-[#222] last:border-0 hover:bg-white/[0.02]">
                          <td className="p-3 text-white text-sm font-mono">{new Date(p.date).toLocaleDateString('fr-FR')}</td>
                          <td className="p-3 text-[#8C8F94] text-sm">{p.site_id}</td>
                          <td className="p-3 text-white text-sm">{p.product}</td>
                          <td className="p-3 text-right text-white font-semibold text-sm">{formatCurrency(p.amount)}</td>
                          <td className="p-3 text-center"><Badge status={p.payment_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* EXCHANGES */}
        {tab === 'exchanges' && (
          <div className="space-y-4" data-testid="tab-content-exchanges">
            {/* Add exchange */}
            <div className="bg-[#111111] rounded-xl border border-[#222] p-4 space-y-3">
              <h4 className="text-white font-semibold text-sm">Nouvel échange</h4>
              <div className="flex gap-3 flex-wrap">
                <select
                  value={newExchange.type}
                  onChange={e => setNewExchange(p => ({...p, type: e.target.value}))}
                  className="bg-[#0a0a0a] border border-[#222] text-white rounded-lg px-3 py-2 text-sm focus:border-[#00E01A] outline-none"
                  data-testid="exchange-type"
                >
                  <option value="email">Email</option>
                  <option value="appel">Appel</option>
                  <option value="note">Note interne</option>
                  <option value="sms">SMS</option>
                </select>
                <Input
                  value={newExchange.sujet}
                  onChange={e => setNewExchange(p => ({...p, sujet: e.target.value}))}
                  placeholder="Sujet (optionnel)"
                  className="bg-[#0a0a0a] border-[#222] text-white flex-1 min-w-[150px] text-sm placeholder:text-[#52555A]"
                  data-testid="exchange-subject"
                />
              </div>
              <textarea
                value={newExchange.contenu}
                onChange={e => setNewExchange(p => ({...p, contenu: e.target.value}))}
                rows={3}
                className="w-full bg-[#0a0a0a] border border-[#222] rounded-lg p-3 text-white text-sm resize-none focus:border-[#00E01A] outline-none placeholder:text-[#52555A]"
                placeholder="Contenu de l'échange..."
                data-testid="exchange-content"
              />
              <Button
                onClick={handleAddExchange}
                disabled={sendingExchange || !newExchange.contenu.trim()}
                className="bg-[#00E01A] hover:bg-[#00B815] text-black text-sm"
                data-testid="add-exchange-btn"
              >
                <Send size={14} className="mr-2" /> {sendingExchange ? 'Envoi...' : 'Ajouter'}
              </Button>
            </div>

            {/* Exchange list */}
            {exchanges.length === 0 ? (
              <div className="bg-[#111111] rounded-xl border border-[#222] p-12 text-center">
                <MessageSquare size={36} className="mx-auto text-[#52555A] mb-3" />
                <p className="text-[#8C8F94]">Aucun échange</p>
              </div>
            ) : (
              <div className="space-y-2">
                {exchanges.map(ex => {
                  const isOutgoing = ex.auteur === 'Riding Watt';
                  return (
                    <div key={ex.id} className={`bg-[#111111] rounded-xl border p-4 ${isOutgoing ? 'border-[#00E01A]/20' : 'border-[#3B82F6]/20'}`} data-testid={`exchange-${ex.id}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${isOutgoing ? 'bg-[#142915] text-[#00E01A]' : 'bg-[rgba(59,130,246,0.15)] text-[#3B82F6]'}`}>
                            {ex.type}
                          </span>
                          <span className="text-white text-sm font-medium">{ex.auteur}</span>
                          {ex.sujet && <span className="text-[#8C8F94] text-xs">— {ex.sujet}</span>}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-[#52555A] text-xs">{new Date(ex.date).toLocaleDateString('fr-FR')}</span>
                          <button onClick={() => handleDeleteExchange(ex.id)} className="p-1 text-[#52555A] hover:text-[#EF4444] transition-colors" data-testid={`delete-exchange-${ex.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      </div>
                      <p className="text-[#8C8F94] text-sm whitespace-pre-wrap">{ex.contenu}</p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
